import { css } from 'lit';
import HTTPMethod from 'http-method-enum';
import { Action } from 'redux';
import { ConfigHHApi } from '@/config/ConfigHH';
import { CurrentEditorsDto } from '../../../typings/api';
import { APIConfig, DebuggingConfig } from '../../config/ConfigCARSx';
import APIRequest, { APIError, APIRequestReturn, getAPIHeaders } from '../APIRequest';
import { RootState, ThunkActionRoot, ThunkDispatchRoot } from '../redux-store';
import { HHActionType, HHState } from './redux-hh';

export interface SetHHCurrentEditors extends Action<typeof HHActionType.SET_HH_CURRENT_EDITORS> {
	currentEditorsDto: HHState['currentEditorsDto'];
}

export const getHHCurrentEditors =
	(id: number): ThunkActionRoot<Promise<APIRequestReturn>> =>
	async (dispatch: ThunkDispatchRoot): Promise<APIRequestReturn> => {
		const url = new URL(ConfigHHApi.currentEditors(id), APIConfig.endpointURLBase);
		const apiRequestReturn = await APIRequest(
			new Request(url.href, {
				method: HTTPMethod.GET,
				headers: new Headers({
					...getAPIHeaders(),
				}),
			}),
		);

		try {
			const currentEditorsDto: CurrentEditorsDto = (await apiRequestReturn.response
				?.clone()
				.json()) as CurrentEditorsDto;
			dispatch({
				type: HHActionType.SET_HH_CURRENT_EDITORS,
				currentEditorsDto,
			});
		} catch (error) {
			apiRequestReturn.apiError = APIError.ResponseUnparseable;
			if (DebuggingConfig.showConsoleLogs) {
				console.error(`error parsing current editors of highway helper #${id} from server:`, error);
			}
		}
		return apiRequestReturn;
	};

export const setHHCurrentEditors =
	(id: number, sessionUUID: string): ThunkActionRoot<Promise<APIRequestReturn>> =>
	async (dispatch: ThunkDispatchRoot): Promise<APIRequestReturn> => {
		const url = new URL(ConfigHHApi.currentEditors(id), APIConfig.endpointURLBase);
		const apiRequestReturn = await APIRequest(
			new Request(url.href, {
				method: HTTPMethod.POST,
				headers: new Headers({
					...getAPIHeaders(),
				}),
				body: JSON.stringify({ sessionUUID }),
			}),
		);

		try {
			const currentEditorsDto: CurrentEditorsDto = (await apiRequestReturn.response
				?.clone()
				.json()) as CurrentEditorsDto;
			dispatch({
				type: HHActionType.SET_HH_CURRENT_EDITORS,
				currentEditorsDto,
			});
		} catch (error) {
			apiRequestReturn.apiError = APIError.ResponseUnparseable;
			if (DebuggingConfig.showConsoleLogs) {
				console.error(`error parsing current editors of event #${id} from server:`, error);
			}
		}
		return apiRequestReturn;
	};

export const clearHHCurrentEditors =
	(id: number, sessionUUID: string): ThunkActionRoot =>
	(dispatch: ThunkDispatchRoot, getState: () => RootState) => {
		if (
			getState().rem.currentEditors?.currentEditors?.some(
				(editor) => editor.sessionUUID === sessionUUID,
			)
		) {
			const url = new URL(ConfigHHApi.currentEditors(id), APIConfig.endpointURLBase);
			void APIRequest(
				new Request(url.href, {
					method: HTTPMethod.DELETE,
					headers: new Headers({
						...getAPIHeaders(),
					}),
					body: JSON.stringify({ sessionUUID }),
				}),
			);
		}

		const currentEditorsDto: CurrentEditorsDto = {
			entityId: undefined,
			currentEditors: [],
			updated: undefined,
		};
		dispatch({
			type: HHActionType.SET_HH_CURRENT_EDITORS,
			currentEditorsDto,
		});
	};
